import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EMessageStatus, type IChatMessage } from '@kognitos/lattice';

import { ChatActivity } from '@/shared/components/ChatActivity';
import { formatDurationUltraCompact } from '@/shared/utils/datetime-utils';

import { workRunSpanMs } from '../utils/work-run-span';

/** Below this, a run is too short for a count to be worth reading. */
export const WORK_BLOCK_ELAPSED_AFTER_MS = 5000;

const ELAPSED_TICK_MS = 1000;

export interface IRainWorkBlockProps {
  /**
   * The run this header stands for, in the order it happened. Read for the
   * duration and the error state only — the rows themselves are rendered by the
   * transcript, not here. Empty while the agent is working with nothing to
   * show for it yet, which is a header with no disclosure behind it.
   */
  rows: IChatMessage[];
  /** Whether the agent is still working inside this run. */
  isRunning: boolean;
  /** Whether the run's rows are currently in the transcript below. */
  isExpanded: boolean;
  /** Asks for the run to be opened or closed. */
  onExpandedChange: (expanded: boolean) => void;
}

/**
 * The header standing in for one stretch of agent work.
 *
 * A HEADER ONLY, deliberately. Opening a run re-emits its rows into the
 * transcript as the ordinary messages they already are (see `foldWorkRuns`),
 * rather than nesting them in a panel — so an open run reads as plain chat, at
 * the transcript's own width and spacing, with its single scrollbar. There is
 * nothing to strip because nothing was wrapped.
 */
export function RainWorkBlock({
  rows,
  isRunning,
  isExpanded,
  onExpandedChange,
}: IRainWorkBlockProps) {
  const { t } = useTranslation('rain');

  const spanMs = workRunSpanMs(rows);

  /*
   * A failure inside a folded run must survive the fold. Collapsing it behind a
   * neutral header would make a lookup that errored read as work that
   * succeeded, and a failure is the one thing in a run nobody should have to
   * click to discover. The header carries the tone and the glyph; the count
   * stays out of the copy, which belongs to the rows.
   */
  const hasError = rows.some(row => row.status === EMessageStatus.ERROR);

  /*
   * Counted only for a run with no rows, and only from this header's own
   * arrival. A run WITH rows settles to its rows' span, a shorter one than
   * wall-clock-to-now, so a count there would jump backwards the moment it
   * settled — and its rows are already proof the agent is alive.
   */
  const appearedAtRef = useRef(Date.now());
  const isCounting = isRunning && rows.length === 0;
  const [elapsedMs, setElapsedMs] = useState(0);
  // eslint-disable-next-line no-restricted-syntax -- reason: a clock the UI displays advances on an interval
  useEffect(() => {
    if (!isCounting) {
      setElapsedMs(0);
      return undefined;
    }
    const tick = () => setElapsedMs(Date.now() - appearedAtRef.current);
    tick();
    const interval = setInterval(tick, ELAPSED_TICK_MS);
    return () => {
      clearInterval(interval);
    };
  }, [isCounting]);

  let title: string;
  if (isRunning) {
    title = t('rain.workBlock.working');
  } else if (spanMs !== undefined) {
    title = t('rain.workBlock.worked', {
      duration: formatDurationUltraCompact(spanMs / 1000),
    });
  } else {
    title = t('rain.workBlock.workedNoDuration');
  }

  let icon: 'Loader2' | 'CircleAlert' | 'Wrench';
  if (isRunning) {
    // The one glyph ChatActivity animates, so a closed header still carries
    // progress on its own.
    icon = 'Loader2';
  } else if (hasError) {
    icon = 'CircleAlert';
  } else {
    icon = 'Wrench';
  }

  const hasRows = rows.length > 0;

  return (
    <ChatActivity
      variant="tool-call"
      title={title}
      titleSuffix={
        elapsedMs >= WORK_BLOCK_ELAPSED_AFTER_MS
          ? t('rain.workBlock.elapsed', {
              duration: formatDurationUltraCompact(elapsedMs / 1000),
            })
          : undefined
      }
      isActive={isRunning}
      isError={hasError}
      collapsible={hasRows}
      compact
      icon={icon}
      expanded={isExpanded}
      onExpandedChange={onExpandedChange}
      data-testid="rain-work-block"
    />
  );
}
